<template>
  <div
    class="confirmation-backdrop"
    @click.self="cancel"
  >
    <section
      class="confirmation-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notification-confirmation-title"
    >
      <header class="confirmation-header">
        <h3 id="notification-confirmation-title">
          {{ heading }}
        </h3>

        <button
          type="button"
          class="confirmation-close"
          aria-label="Cerrar"
          :disabled="loading"
          @click="cancel"
        >
          ×
        </button>
      </header>

      <div class="confirmation-body">
        <p class="confirmation-intro">
          Revisa cómo se procesará
          <strong>{{ notificationTitle }}</strong>:
        </p>

        <dl class="confirmation-summary">
          <div>
            <dt>Activa en el sistema</dt>
            <dd :class="activeInSystem ? 'status-yes' : 'status-no'">
              {{ activeInSystem ? 'Sí' : 'No' }}
            </dd>
          </div>

          <div>
            <dt>{{ emailLabel }}</dt>
            <dd :class="emailWillBeSent ? 'status-yes' : 'status-no'">
              {{ emailWillBeSent ? 'Sí' : 'No' }}
            </dd>
          </div>

          <div>
            <dt>Destinatarios seleccionados</dt>
            <dd>{{ recipientCount }}</dd>
          </div>

          <div v-if="resetReadState">
            <dt>Estado de lectura en el sistema</dt>
            <dd class="status-unread">Se marcará como no leída</dd>
          </div>
        </dl>

        <p
          v-if="emailWillBeSent && !activeInSystem"
          class="independent-channels-note"
        >
          El correo se enviará aunque la notificación no quede activa
          en el sistema.
        </p>

        <p class="confirmation-question">
          ¿Deseas continuar?
        </p>
      </div>

      <footer class="confirmation-footer">
        <button
          type="button"
          class="confirmation-button button-secondary"
          :disabled="loading"
          @click="cancel"
        >
          Cancelar
        </button>

        <button
          type="button"
          class="confirmation-button button-primary"
          :disabled="loading"
          @click="$emit('confirm')"
        >
          {{ loading ? loadingLabel : confirmLabel }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'NotificationConfirmationModal',
  props: {
    heading: {
      type: String,
      required: true
    },
    notificationTitle: {
      type: String,
      required: true
    },
    activeInSystem: {
      type: Boolean,
      required: true
    },
    emailWillBeSent: {
      type: Boolean,
      required: true
    },
    emailLabel: {
      type: String,
      default: 'Envío por correo'
    },
    recipientCount: {
      type: Number,
      required: true
    },
    confirmLabel: {
      type: String,
      required: true
    },
    loadingLabel: {
      type: String,
      default: 'Procesando...'
    },
    loading: {
      type: Boolean,
      default: false
    },
    resetReadState: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    cancel() {
      if (!this.loading) {
        this.$emit('cancel')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.confirmation-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1em;
  background: rgba(18, 43, 61, 0.6);
}

.confirmation-modal {
  width: 100%;
  max-width: 500px;
  overflow: hidden;
  border: 1px solid var(--color-fog);
  border-radius: 6px;
  background: var(--color-white);
}

.confirmation-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1em;
  padding: 1em 1.2em;
  background: var(--color-navy);
  color: var(--color-white);
}

.confirmation-header h3 {
  margin: 0;
  color: inherit;
  font-size: 1em;
}

.confirmation-close {
  width: 29px;
  height: 29px;
  border: 1px solid var(--color-steel);
  border-radius: 4px;
  background: transparent;
  color: var(--color-white);
  font-size: 1.2em;
  cursor: pointer;
}

.confirmation-close:hover:not(:disabled) {
  border-color: var(--color-orange);
  color: var(--color-orange);
}

.confirmation-body {
  padding: 1.2em;
  color: var(--color-graphite);
  font-size: 0.88em;
}

.confirmation-intro {
  margin: 0 0 1em;
}

.confirmation-summary {
  margin: 0;
  border: 1px solid var(--color-fog);
  border-radius: 4px;
}

.confirmation-summary div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1em;
  padding: 0.65em 0.75em;
}

.confirmation-summary div + div {
  border-top: 1px solid var(--color-fog);
}

.confirmation-summary dt {
  color: var(--color-graphite);
}

.confirmation-summary dd {
  margin: 0;
  color: var(--color-navy);
  font-weight: 700;
}

.confirmation-summary .status-yes {
  color: var(--color-success);
}

.confirmation-summary .status-no {
  color: var(--color-ghost);
}

.confirmation-summary .status-unread {
  color: var(--color-orange);
  text-align: right;
}

.independent-channels-note {
  margin: 0.9em 0 0;
  padding: 0.65em 0.75em;
  border-left: 3px solid var(--color-orange);
  background: var(--color-orange-light, #fff7f1);
  line-height: 1.4;
}

.confirmation-question {
  margin: 1em 0 0;
  color: var(--color-navy);
  font-weight: 700;
}

.confirmation-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.7em;
  padding: 0.9em 1.2em;
  border-top: 1px solid var(--color-fog);
  background: var(--color-mist);
}

.confirmation-button {
  min-height: 35px;
  padding: 0.5em 1em;
  border: 1px solid;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.82em;
  font-weight: 600;
  cursor: pointer;
}

.button-secondary {
  border-color: var(--color-steel);
  background: var(--color-white);
  color: var(--color-steel);
}

.button-primary {
  border-color: var(--color-steel);
  background: var(--color-steel);
  color: var(--color-white);
}

.confirmation-button:disabled,
.confirmation-close:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (max-width: 520px) {
  .confirmation-footer {
    flex-direction: column-reverse;
  }

  .confirmation-button {
    width: 100%;
  }
}
</style>
